/* eslint-disable max-len */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import {
  CCard, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CPagination, CPaginationItem, CTooltip, CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
  CButton, CBadge, CRow, CCol, CFormInput, CFormSelect, CCollapse
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilTrash, cilFilter, cilX, cilSearch, cilArrowTop, cilArrowBottom } from '@coreui/icons';
import { mainColors } from '../../helpers/colors';
import { formatDateOnly } from '../../helpers/dateHelper';
import {
  deleteShipmentRequest, getShipmentRequest
} from '../../store/shipment/shipmentActions';
import './ShipmentTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import ExportData from '../ExportData/ExportData';
import { formatScaledAmount } from '../../helpers/quantity';
import { toScaledInteger } from '../../helpers/quantity';
import { getSupplier } from '../../services/endpoints';

function ShipmentTable(props) {
  const {
    shipmentList,
    isLoading,
    deleteShipmentRequest,
    // Sayfalama alanları (Redux'dan)
    totalCount,
    pageNumber: serverPageNumber,
    pageSize: serverPageSize,
    totalPages: serverTotalPages,
    hasPreviousPage,
    hasNextPage
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['index', 'OrderNo', 'OpsCustomerName', 'WeaveCode', 'ProductShippedQuantity', 'NetWeight', 'GrossWeight', 'Lot', 'RollCount', 'ShipmentDatetime', 'DeliveryNo', 'actions'], 'ShipmentTable');

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    OrderNo: '',
    OpsCustomerName: '',
    WeaveCode: '',
    ShipmentDatetimeStart: '',
    ShipmentDatetimeEnd: '',
    DeliveryNo: '',
    OpsSupplierGuid: '',
    ProductShippedQuantityWhole: '',
    ProductShippedQuantityDecimal: '',
    RollCount: ''
  });

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('Guid');
  const [supplierOptions, setSupplierOptions] = useState([]);

  // DDMMYYYY formatına dönüştür
  const formatDateToDDMMYYYY = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return parseInt(`${dd}${mm}${yyyy}`, 10);
  };

  const toScaledFilterQuantity = (whole, decimal) => {
    const wholeValue = String(whole || '').replace(/\D/g, '');
    const decimalValue = String(decimal || '').replace(/\D/g, '').slice(0, 2);

    if (!wholeValue && !decimalValue) {
      return null;
    }

    const wholePart = wholeValue ? parseInt(wholeValue, 10) : 0;
    const decimalPart = decimalValue ? parseInt(decimalValue.padEnd(2, '0'), 10) : 0;
    return (wholePart * 100) + decimalPart;
  };

  const handleDecimalInput = (e, field) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleWholeInput = (e, field) => {
    const value = e.target.value.replace(/\D/g, '');
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handlePriceKeyDown = (e, decimalField) => {
    const isDotOrComma = e.key === '.' || e.key === ',' ||
      e.keyCode === 190 || e.keyCode === 110 || e.keyCode === 188;
    if (isDotOrComma) {
      e.preventDefault();
      const decimalInput = document.querySelector(`input[name="${decimalField}"]`);
      if (decimalInput) {
        decimalInput.focus();
        decimalInput.select();
      }
    }
  };

  // API'ye istek gönder
  const fetchShipments = useCallback((pageNum = 1, pageSz = rowsPerPage) => {
    const trimmedOrderNo = filters.OrderNo.trim();
    const trimmedCustomerName = filters.OpsCustomerName.trim();
    const trimmedProductCode = filters.WeaveCode.trim();
    const hasShortOrderNo = trimmedOrderNo.length > 0 && trimmedOrderNo.length < 3;
    const hasShortCustomerName = trimmedCustomerName.length > 0 && trimmedCustomerName.length < 3;
    const hasShortProductCode = trimmedProductCode.length > 0 && trimmedProductCode.length < 3;
    if (hasShortOrderNo || hasShortCustomerName || hasShortProductCode) {
      return;
    }

    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    const userId = sessionUserId || userData?.sessionUserId;

    const payload = {
      SessionUserId: String(userId),
      PageNumber: pageNum,
      PageSize: pageSz,
      OrderBy: orderBy || 'Guid',
      IsDescending: order === 'desc',
    };

    // Filtreler
    if (trimmedOrderNo) {
      payload.OrderNo = trimmedOrderNo;
    }
    if (filters.DeliveryNo?.trim()) {
      payload.DeliveryNo = filters.DeliveryNo.trim();
    }
    if (trimmedCustomerName) {
      payload.CustomerName = trimmedCustomerName;
    }
    if (trimmedProductCode) {
      payload.WeaveCode = trimmedProductCode;
    }
    if (filters.OpsSupplierGuid) {
      payload.OpsSupplierGuid = filters.OpsSupplierGuid;
    }
    const normalizedShippedQuantity = toScaledFilterQuantity(filters.ProductShippedQuantityWhole, filters.ProductShippedQuantityDecimal);
    if (normalizedShippedQuantity !== null) {
      payload.ProductShippedQuantity = normalizedShippedQuantity;
    }
    const normalizedRollCount = String(filters.RollCount || '').trim();
    if (normalizedRollCount !== '') {
      const parsedRollCount = parseInt(normalizedRollCount, 10);
      if (!Number.isNaN(parsedRollCount)) {
        payload.RollCount = parsedRollCount;
      }
    }
    if (filters.ShipmentDatetimeStart) {
      const startDate = formatDateToDDMMYYYY(filters.ShipmentDatetimeStart);
      if (startDate) payload.ShipmentStartDate = startDate;
    }
    if (filters.ShipmentDatetimeEnd) {
      const endDate = formatDateToDDMMYYYY(filters.ShipmentDatetimeEnd);
      if (endDate) payload.ShipmentEndDate = endDate;
    }

    dispatch(getShipmentRequest(payload));
  }, [dispatch, filters, order, orderBy, rowsPerPage]);

  // Component mount'ta verileri çek
  useEffect(() => {
    fetchShipments(1, rowsPerPage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const sessionUserId = localStorage.getItem('sessionUserId');
        const response = await getSupplier({ SessionUserId: sessionUserId });
        setSupplierOptions(response?.data?.SupplierList || []);
      } catch (err) {
        setSupplierOptions([]);
      }
    };

    fetchSuppliers();
  }, []);
  // Filtre değiştiğinde otomatik istek at (debounce ile)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      fetchShipments(1, rowsPerPage);
    }, 400); // 400ms debounce

    return () => clearTimeout(timer);
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sayfa Redux'dan geldiğinde local state'i güncelle
  useEffect(() => {
    if (serverPageNumber) setPage(serverPageNumber);
  }, [serverPageNumber]);

  const handleEdit = (item) => {
    navigate(`/shipments/edit/${item.ShipmentGuid}`, { state: { shipment: item } });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;
      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          ShipmentId: deleteModal.item.ShipmentGuid
        };
        deleteShipmentRequest(deleteData);
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    fetchShipments(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
    fetchShipments(1, value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Filter uygula butonu
  const applyFilters = () => {
    setPage(1);
    fetchShipments(1, rowsPerPage);
  };

  const clearFilters = () => {
    const clearedFilters = {
      OrderNo: '',
      OpsCustomerName: '',
      WeaveCode: '',
      ShipmentDatetimeStart: '',
      ShipmentDatetimeEnd: '',
      DeliveryNo: '',
      OpsSupplierGuid: '',
      ProductShippedQuantityWhole: '',
      ProductShippedQuantityDecimal: '',
      RollCount: ''
    };
    setFilters(clearedFilters);
    setPage(1);
    // Filtreleri temizledikten sonra yeni request
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    const userId = sessionUserId || userData?.sessionUserId;
    dispatch(getShipmentRequest({
      SessionUserId: String(userId),
      PageNumber: 1,
      PageSize: rowsPerPage,
      OrderBy: orderBy || 'Guid',
      IsDescending: order === 'desc',
    }));
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleRequestSort = (property) => {
    const trimmedOrderNo = filters.OrderNo.trim();
    const trimmedCustomerName = filters.OpsCustomerName.trim();
    const trimmedProductCode = filters.WeaveCode.trim();
    const hasShortOrderNo = trimmedOrderNo.length > 0 && trimmedOrderNo.length < 3;
    const hasShortCustomerName = trimmedCustomerName.length > 0 && trimmedCustomerName.length < 3;
    const hasShortProductCode = trimmedProductCode.length > 0 && trimmedProductCode.length < 3;
    if (hasShortOrderNo || hasShortCustomerName || hasShortProductCode) {
      return;
    }

    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    // Sıralama değiştiğinde API'ye yeni istek gönder
    setPage(1);
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    const sessionUserId = localStorage.getItem('sessionUserId');
    const userId = sessionUserId || userData?.sessionUserId;

    const payload = {
      SessionUserId: String(userId),
      PageNumber: 1,
      PageSize: rowsPerPage,
      OrderBy: property || 'Guid',
      IsDescending: newOrder === 'desc',
    };

    // Mevcut filtreleri ekle
    if (trimmedOrderNo) {
      payload.OrderNo = trimmedOrderNo;
    }
    if (filters.DeliveryNo?.trim()) {
      payload.DeliveryNo = filters.DeliveryNo.trim();
    }
    if (trimmedCustomerName) {
      payload.CustomerName = trimmedCustomerName;
    }
    if (trimmedProductCode) {
      payload.WeaveCode = trimmedProductCode;
    }
    if (filters.OpsSupplierGuid) {
      payload.OpsSupplierGuid = filters.OpsSupplierGuid;
    }
    const normalizedShippedQuantity = toScaledFilterQuantity(filters.ProductShippedQuantityWhole, filters.ProductShippedQuantityDecimal);
    if (normalizedShippedQuantity !== null) {
      payload.ProductShippedQuantity = normalizedShippedQuantity;
    }
    const normalizedRollCount = String(filters.RollCount || '').trim();
    if (normalizedRollCount !== '') {
      const parsedRollCount = parseInt(normalizedRollCount, 10);
      if (!Number.isNaN(parsedRollCount)) {
        payload.RollCount = parsedRollCount;
      }
    }
    if (filters.ShipmentDatetimeStart) {
      const startDate = formatDateToDDMMYYYY(filters.ShipmentDatetimeStart);
      if (startDate) payload.ShipmentStartDate = startDate;
    }
    if (filters.ShipmentDatetimeEnd) {
      const endDate = formatDateToDDMMYYYY(filters.ShipmentDatetimeEnd);
      if (endDate) payload.ShipmentEndDate = endDate;
    }

    dispatch(getShipmentRequest(payload));
  };

  const columns = [
    { id: 'index', label: '#', sortable: false, filterable: false },
    { id: 'OrderNo', label: t('orderNo') || 'Order No', sortable: true, filterable: true },
    { id: 'OpsCustomerName', label: t('customerName'), sortable: true, filterable: true },
    { id: 'WeaveCode', label: t('productCode') || t('weaveCode'), sortable: true, filterable: true },
    { id: 'ProductShippedQuantity', label: t('productShippedQuantity'), sortable: true, filterable: 'decimal' },
    { id: 'NetWeight', label: t('netWeight') || 'Net Weight', sortable: false, filterable: false },
    { id: 'GrossWeight', label: t('grossWeight') || 'Gross Weight', sortable: false, filterable: false },
    { id: 'Lot', label: t('lot') || 'Lot', sortable: false, filterable: false },
    { id: 'RollCount', label: t('shipmentRollCount'), sortable: false, filterable: 'integer' },
    { id: 'ShipmentDatetime', label: t('shipmentDatetime'), sortable: true, filterable: 'dateRange' },
    { id: 'DeliveryNo', label: t('deliveryNo'), sortable: true, filterable: true },
    { id: 'actions', label: t('actions'), sortable: false, filterable: false }
  ];

  // Server'dan gelen veriyi doğrudan kullan (filtreleme artık sunucu tarafında)
  const filteredData = shipmentList;

  // Server-side pagination: veriyi doğrudan kullan
  const getSortedData = () => {
    return filteredData;
  };

  const totalRows = totalCount;
  const totalPages = serverTotalPages;
  const paginationItems = [];
  const maxVisiblePages = 7;
  let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  if (startPage > 1) {
    paginationItems.push(
      <CPaginationItem key="first" onClick={() => handleChangePage(1)}>
        1
      </CPaginationItem>
    );

    if (startPage > 2) {
      paginationItems.push(
        <CPaginationItem key="start-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    paginationItems.push(
      <CPaginationItem key={i} active={i === page} onClick={() => handleChangePage(i)}>
        {i}
      </CPaginationItem>
    );
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      paginationItems.push(
        <CPaginationItem key="end-ellipsis" disabled>
          ...
        </CPaginationItem>
      );
    }
    paginationItems.push(
      <CPaginationItem key="last" onClick={() => handleChangePage(totalPages)}>
        {totalPages}
      </CPaginationItem>
    );
  }

  const activeFilterCount = Object.entries(filters)
    .filter(([key, value]) => !['ProductShippedQuantityWhole', 'ProductShippedQuantityDecimal'].includes(key) && String(value || '').trim() !== '')
    .length
    + ((String(filters.ProductShippedQuantityWhole || '').trim() || String(filters.ProductShippedQuantityDecimal || '').trim()) ? 1 : 0);

  const exportColumns = useMemo(() => {
    return columns.filter(col => !['actions', 'index'].includes(col.id));
  }, [columns]);

  const buildCellValue = (colId, item) => {
    switch (colId) {
      case 'ShipmentDatetime':
        return formatDateOnly(item[colId]);
      case 'ProductShippedQuantity':
        return formatScaledAmount(item[colId]);
      case 'NetWeight':
      case 'GrossWeight':
        return formatScaledAmount(item[colId]);
      case 'Lot':
        return item[colId] || '';
      case 'RollCount':
        return item[colId] || '';
      case 'WeaveCode':
        // Display WeaveCode if exists, otherwise show YarnCode - YarnName
        return item.WeaveCode || (item.YarnCode && item.YarnName ? `${item.YarnCode} - ${item.YarnName}` : '');
      case 'OrderNo':
        return item.OrderNo || '';
      default:
        return item[colId] ?? '';
    }
  };

  return (
    <div className="shipment-table-container">
      <CCard>
        <CCardBody>
          <div className="filter-controls mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <CButton
                  color={showFilters ? "primary" : "outline-primary"}
                  variant={showFilters ? "solid" : "outline"}
                  size="sm"
                  onClick={toggleFilters}
                  className="filter-toggle-btn"
                >
                  <CIcon icon={cilFilter} size="sm" className="me-1" />
                  {t('filters')}
                  {activeFilterCount > 0 && (
                    <span className="filter-count-badge ms-1">{activeFilterCount}</span>
                  )}
                </CButton>
                {activeFilterCount > 0 && (
                  <CButton
                    color="outline-secondary"
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <CIcon icon={cilX} size="sm" className="me-1" />
                    {t('clearFilters')}
                  </CButton>
                )}
              </div>
              <div className="results-info d-flex align-items-center gap-2">
                <span className="text-muted">
                  {totalRows} {t('resultsFound')}
                </span>
                <ExportData
                  columns={exportColumns}
                  rows={getSortedData()}
                  allRows={shipmentList}
                  buildCellValue={buildCellValue}
                  filenamePrefix="shipments_export"
                  triggerDisabled={!filteredData || filteredData.length === 0}
                />
              </div>
            </div>
          </div>

          {/* Filtre Alanları */}
          <CCollapse visible={showFilters}>
            <div className="filter-panel mb-4">
              <CRow className="g-3">
                {columns.filter(col => col.filterable).map((column) => {
                  if (column.filterable === 'dateRange') {
                    return (
                      <React.Fragment key={column.id}>
                        <CCol xs={12} sm={6} md={4} lg={3}>
                          <div className="filter-field">
                            <label className="filter-label">{'Sevkiyat Başlangıç Tarihi'}</label>
                            <CFormInput
                              type="date"
                              value={filters.ShipmentDatetimeStart}
                              onChange={(e) => handleFilterChange('ShipmentDatetimeStart', e.target.value)}
                              className="filter-input"
                              size="sm"
                            />
                          </div>
                        </CCol>
                        <CCol xs={12} sm={6} md={4} lg={3}>
                          <div className="filter-field">
                            <label className="filter-label">{'Sevkiyat Bitiş Tarihi'}</label>
                            <CFormInput
                              type="date"
                              value={filters.ShipmentDatetimeEnd}
                              onChange={(e) => handleFilterChange('ShipmentDatetimeEnd', e.target.value)}
                              className="filter-input"
                              size="sm"
                            />
                          </div>
                        </CCol>
                      </React.Fragment>
                    );
                  }

                  if (column.filterable === 'decimal' && column.id === 'ProductShippedQuantity') {
                    return (
                      <CCol key={column.id} xs={12} sm={6} md={4} lg={3}>
                        <div className="filter-field">
                          <label className="filter-label">{column.label}</label>
                          <div className="decimal-input-group">
                            <CFormInput
                              type="text"
                              name="FilterProductShippedQuantityWhole"
                              className="filter-input whole-input"
                              value={filters.ProductShippedQuantityWhole}
                              onChange={(e) => handleWholeInput(e, 'ProductShippedQuantityWhole')}
                              onKeyDown={(e) => handlePriceKeyDown(e, 'FilterProductShippedQuantityDecimal')}
                              size="sm"
                              placeholder="0"
                            />
                            <span className="decimal-separator">,</span>
                            <CFormInput
                              type="text"
                              name="FilterProductShippedQuantityDecimal"
                              className="filter-input decimal-input"
                              value={filters.ProductShippedQuantityDecimal}
                              onChange={(e) => handleDecimalInput(e, 'ProductShippedQuantityDecimal')}
                              size="sm"
                              placeholder="00"
                            />
                          </div>
                        </div>
                      </CCol>
                    );
                  }

                  if (column.filterable === 'integer' && column.id === 'RollCount') {
                    return (
                      <CCol key={column.id} xs={12} sm={6} md={4} lg={3}>
                        <div className="filter-field">
                          <label className="filter-label">{column.label}</label>
                          <div className="filter-input-wrapper">
                            <CIcon icon={cilSearch} className="filter-input-icon" />
                            <CFormInput
                              type="text"
                              placeholder={`${t('search')} ${column.label.toLowerCase()}...`}
                              value={filters.RollCount}
                              onChange={(e) => handleWholeInput(e, 'RollCount')}
                              className="filter-input"
                              size="sm"
                            />
                            {filters.RollCount && (
                              <CButton
                                color="link"
                                size="sm"
                                className="filter-clear-btn"
                                onClick={() => handleFilterChange('RollCount', '')}
                              >
                                <CIcon icon={cilX} size="sm" />
                              </CButton>
                            )}
                          </div>
                        </div>
                      </CCol>
                    );
                  }

                  return (
                    <CCol key={column.id} xs={12} sm={6} md={4} lg={3}>
                      <div className="filter-field">
                        <label className="filter-label">{column.label}</label>
                        <div className="filter-input-wrapper">
                          <CIcon icon={cilSearch} className="filter-input-icon" />
                          <CFormInput
                            type="text"
                            placeholder={`${t('search')} ${column.label.toLowerCase()}...`}
                            value={filters[column.id]}
                            onChange={(e) => handleFilterChange(column.id, e.target.value)}
                            className="filter-input"
                            size="sm"
                          />
                          {filters[column.id] && (
                            <CButton
                              color="link"
                              size="sm"
                              className="filter-clear-btn"
                              onClick={() => handleFilterChange(column.id, '')}
                            >
                              <CIcon icon={cilX} size="sm" />
                            </CButton>
                          )}
                        </div>
                      </div>
                    </CCol>
                  );
                })}
                <CCol xs={12} sm={6} md={4} lg={3}>
                  <div className="filter-field">
                    <label className="filter-label">Fason Dokuma Tedarikçisi</label>
                    <CFormSelect
                      value={filters.OpsSupplierGuid}
                      onChange={(e) => handleFilterChange('OpsSupplierGuid', e.target.value)}
                      className="filter-input"
                      size="sm"
                    >
                      <option value="">{t('select')}</option>
                      {supplierOptions.map((item) => (
                        <option key={item.SupplierId} value={item.SupplierId}>
                          {item.SupplierName}
                        </option>
                      ))}
                    </CFormSelect>
                  </div>
                </CCol>
              </CRow>
            </div>
          </CCollapse>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell key={column.id} className="table-header-cell" style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }} onClick={() => column.sortable && handleRequestSort(column.id)}>
                      {column.label}
                      {column.sortable && orderBy === column.id && (
                        <CIcon icon={order === 'asc' ? cilArrowTop : cilArrowBottom} size="sm" className="ms-1" />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {getSortedData().length > 0 ? (
                <>
                  {getSortedData().map((item, index) => (
                    <CTableRow key={item.ShipmentGuid || index}>
                      <CTableDataCell>{(page - 1) * rowsPerPage + index + 1}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color="primary" shape="rounded-pill">
                          {item.OrderNo || '-'}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>{item.OpsCustomerName}</CTableDataCell>
                      <CTableDataCell>
                        {item.WeaveCode || (item.YarnCode && item.YarnName ? `${item.YarnCode} - ${item.YarnName}` : '')}
                      </CTableDataCell>
                      <CTableDataCell>{formatScaledAmount(item.ProductShippedQuantity)}</CTableDataCell>
                      <CTableDataCell>{formatScaledAmount(item.NetWeight) || '-'}</CTableDataCell>
                      <CTableDataCell>{formatScaledAmount(item.GrossWeight) || '-'}</CTableDataCell>
                      <CTableDataCell>{item.Lot || '-'}</CTableDataCell>
                      <CTableDataCell>{item.RollCount || '-'}</CTableDataCell>
                      <CTableDataCell>{formatDateOnly(item.ShipmentDatetime)}</CTableDataCell>
                      <CTableDataCell>{item.DeliveryNo}</CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex gap-2">
                          <CTooltip content={t('editShipment')}>
                            <div className="cursor-pointer p-1" onClick={() => handleEdit(item)}>
                              <CIcon icon={cilPencil} size="sm" style={{ color: mainColors.primary }} />
                            </div>
                          </CTooltip>
                          <CTooltip content={t('deleteShipment')}>
                            <div className="cursor-pointer p-1" onClick={() => handleDeleteClick(item)}>
                              <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                            </div>
                          </CTooltip>
                        </div>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                  {activeFilterCount > 0 && (
                    <CTableRow>
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell>
                        <span className="text-muted">{t('total') || 'toplam'}: </span>
                        <strong>{formatScaledAmount(filteredData.reduce((sum, it) => sum + (toScaledInteger(it.ProductShippedQuantity) || 0), 0))}</strong>
                      </CTableDataCell>
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                      <CTableDataCell />
                    </CTableRow>
                  )}
                </>
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center py-4">
                    <div className="no-data-message">
                      {isLoading ? (
                        <div className="loading-message">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          {t('loadingData')}
                        </div>
                      ) : activeFilterCount > 0 ? (
                        <div className="no-results-message">
                          <CIcon icon={cilSearch} size="xl" className="mb-2 text-muted" />
                          <div>{t('noResultsFound')}</div>
                          <CButton
                            color="link"
                            size="sm"
                            onClick={clearFilters}
                            className="mt-2"
                          >
                            {t('clearFiltersToSeeAll')}
                          </CButton>
                        </div>
                      ) : (
                        <div className="text-muted">{t('noDataFound')}</div>
                      )}
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('show')}:</span>
                <select className="form-select form-select-sm" value={rowsPerPage} onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))} style={{ width: 'auto' }}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>
              <CPagination className="mb-0">
                <CPaginationItem onClick={() => page > 1 && handleChangePage(page - 1)} disabled={page === 1}>&laquo;</CPaginationItem>
                {paginationItems}
                <CPaginationItem onClick={() => page < totalPages && handleChangePage(page + 1)} disabled={page === totalPages}>&raquo;</CPaginationItem>
              </CPagination>
              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>
      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('deleteShipment')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteShipment')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('unitName')}:</strong> {deleteModal.item.UnitName}<br />
              <strong>{t('billingCompany')}:</strong> {deleteModal.item.BillingCompany}<br />
              <strong>{t('deliveryNo')}:</strong> {deleteModal.item.DeliveryNo}<br />
              <strong>{t('shipmentDatetime')}:</strong> {formatDateOnly(deleteModal.item.ShipmentDatetime)}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('shipmentDeleteWarning')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>{t('cancel')}</CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>{t('delete')}</CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => ({
  shipmentList: state.shipmentReducer.shipmentList || [],
  isLoading: state.shipmentReducer.isLoading,
  // Sayfalama alanları
  totalCount: state.shipmentReducer.totalCount || 0,
  pageNumber: state.shipmentReducer.pageNumber || 1,
  pageSize: state.shipmentReducer.pageSize || 10,
  totalPages: state.shipmentReducer.totalPages || 0,
  hasPreviousPage: state.shipmentReducer.hasPreviousPage || false,
  hasNextPage: state.shipmentReducer.hasNextPage || false,
});

const mapDispatchToProps = (dispatch) => ({
  deleteShipmentRequest: (payload) => dispatch(deleteShipmentRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShipmentTable); 
